import { useCallback, useRef, useState } from "react";
import {
  MAX_SEGMENT_CONDITIONS,
  type ConditionCategory,
  type ConditionOperator,
  type SegmentCondition,
  type SegmentConditionDraft,
  type SegmentRules,
} from "./CreateSegment.Constants";
import { createCondition, createEmptyCondition, withConditionId } from "./CreateSegment.Utils";

const FIRST_CONDITION_ID = 1;

const INITIAL_RULES: SegmentRules = {
  conditions: [createEmptyCondition(FIRST_CONDITION_ID)],
  operators: [],
};

export const useSegmentConditions = () => {
  const [rules, setRules] = useState<SegmentRules>(INITIAL_RULES);
  const nextIdRef = useRef(FIRST_CONDITION_ID + 1);

  const takeId = useCallback(() => {
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    return id;
  }, []);

  const replaceCondition = useCallback((next: SegmentCondition) => {
    setRules(prev => ({
      ...prev,
      conditions: prev.conditions.map(condition => (condition.id === next.id ? next : condition)),
    }));
  }, []);

  const changeCategory = useCallback(
    (id: number, category: ConditionCategory) => replaceCondition(createCondition(category, id)),
    [replaceCondition]
  );

  const addCondition = useCallback(
    (operator: ConditionOperator) => {
      const id = takeId();
      setRules(prev =>
        prev.conditions.length >= MAX_SEGMENT_CONDITIONS
          ? prev
          : {
              conditions: [...prev.conditions, createEmptyCondition(id)],
              operators: [...prev.operators, operator],
            }
      );
    },
    [takeId]
  );

  const duplicateCondition = useCallback(
    (id: number) => {
      const copyId = takeId();
      setRules(prev => {
        const index = prev.conditions.findIndex(condition => condition.id === id);
        if (index === -1 || prev.conditions.length >= MAX_SEGMENT_CONDITIONS) {
          return prev;
        }
        const conditions = [...prev.conditions];
        const operators = [...prev.operators];
        conditions.splice(index + 1, 0, { ...prev.conditions[index], id: copyId });
        operators.splice(index, 0, "and");
        return { conditions, operators };
      });
    },
    [takeId]
  );

  const removeCondition = useCallback(
    (id: number) => {
      const replacementId = takeId();
      setRules(prev => {
        const index = prev.conditions.findIndex(condition => condition.id === id);
        if (index === -1) {
          return prev;
        }
        if (prev.conditions.length === 1) {
          return { conditions: [createEmptyCondition(replacementId)], operators: [] };
        }
        const junctionIndex = index === 0 ? 0 : index - 1;
        return {
          conditions: prev.conditions.filter(condition => condition.id !== id),
          operators: prev.operators.filter((_, operatorIndex) => operatorIndex !== junctionIndex),
        };
      });
    },
    [takeId]
  );

  const toggleOperator = useCallback((index: number) => {
    setRules(prev => ({
      ...prev,
      operators: prev.operators.map((operator, operatorIndex) =>
        operatorIndex === index ? (operator === "and" ? "or" : "and") : operator
      ),
    }));
  }, []);

  const fillConditions = useCallback(
    (drafts: SegmentConditionDraft[], operators: ConditionOperator[]) => {
      const conditions = drafts
        .slice(0, MAX_SEGMENT_CONDITIONS)
        .map(draft => withConditionId(draft, takeId()));
      setRules({ conditions, operators: operators.slice(0, conditions.length - 1) });
    },
    [takeId]
  );

  return {
    rules,
    replaceCondition,
    changeCategory,
    addCondition,
    duplicateCondition,
    removeCondition,
    toggleOperator,
    fillConditions,
    restoreRules: setRules,
  };
};
